import { WorkOrderItemData } from '../index';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import { message, notification } from 'antd';
import { useDisclosure } from '@/hooks';
import { useWorkorderItemPermissions } from '../../WorkOrderItemPartForm/workorderItemPermissions';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import WorkOrderItemSpan from '../WorkOrderItemSpan';
import Authorities from '@/types/Authority';
import formatterPrice from '@/utils/number/formatterPrice';
import InputDecimalCell from '@/components/_common/InputDecimalCell';
import useWorkOrderItemUpdate from '../WorkOrderItemUtils/useWorkOrderItemUpdate';
import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { usePartStockPrices } from '@/core/domain/stock/StockItems/StockItems.hooks';
import { useMemo } from 'react';

interface Props {
  item: WorkOrderItemData;
}

export default function WorkOrderItemUnitPrice(props: Props) {
  const [isInputEnabled, inputAction] = useDisclosure(false);
  const itemPermissions = useWorkorderItemPermissions();

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const isPart = props.item.type === WorkOrderItemType.Part;

  const update = useWorkOrderItemUpdate(props.item);

  const partStockPrices = usePartStockPrices(
    props.item.id,
    isPart && isInputEnabled,
  );

  const stockPrices = useMemo(
    () =>
      partStockPrices.data?.result.find(
        (part) => part.company.id === workorder?.company.id,
      ),
    [workorder?.company.id, partStockPrices, partStockPrices.dataUpdatedAt],
  );

  const hasLowValuePermission = hasPermissions([
    Authorities.MOVIMENTATION_WORK_ORDER_ALLOW_TO_ENTER_ITEM_SALES_VALUE_BELOW_PROFIT_MARGIN,
  ]);

  const handleUpdateUnitPrice = async (unitPrice: number) => {
    if (!itemPermissions.allowSellingBellowCostValue) {
      const subTotal = (props.item?.quantity || 0) * (unitPrice || 0);

      const amount = subTotal - (props.item?.discount || 0) * 100;

      const sellPrice = stockPrices?.sellPrice || 0;

      if (!hasLowValuePermission && unitPrice < sellPrice) {
        return notification.info({
          message: `Você não tem permissão para lançar valor total de venda do item abaixo da margem de lucro ${formatterPrice(
            sellPrice || 0,
          )}`,
        });
      }

      if (amount < (props.item?.unitCost || 0) * (props.item.quantity || 0)) {
        message.destroy();
        return message.warn(
          'O preço de venda deve ser maior que o custo unitário',
        );
      }
    }

    try {
      await update.mutateAsync({ key: 'unitPrice', value: unitPrice });
    } finally {
      inputAction.close();
    }
  };

  if (isInputEnabled) {
    return (
      <InputDecimalCell
        defaultValue={props.item.unitPrice}
        min={0}
        loading={update.isLoading}
        onConfirmValue={(value) => handleUpdateUnitPrice(value)}
        onClose={inputAction.close}
      />
    );
  }

  return (
    <WorkOrderItemSpan
      item={props.item}
      onClick={inputAction.open}
      permission={
        props.item.disableUnitPrice ||
        (complement?.status !== WorkOrderComplementStatus.Open &&
          !hasPermissions([
            Authorities.MOVIMENTATION_WORKORDER_OPENED_ITEM_CHANGE_VALUE,
          ])) ||
        !hasPermissions([
          props.item.type === WorkOrderItemType.Part
            ? Authorities.MOVIMENTATION_WORKORDER_PART_EDIT_SALE_VALUE
            : Authorities.MOVIMENTATION_WORKORDER_SERVICE_EDIT_SALE_VALUE,
        ])
      }
    >
      {formatterPrice(props.item.unitPrice)}
    </WorkOrderItemSpan>
  );
}
